import React, {
  createRef,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as reactUse from 'react-use'
import { DrawControllerWrapper } from '@scene'
import { every } from 'lodash-es'
import { Object3D, type Intersection } from 'three'
import useTapelineMutations from '@editor/hooks/useTapelineMutations'
import { DEFAULT_TAPLINE_STYLE } from '@editor/stores/tapelineV2Store/utils'
import {
  type DrawControllerRef,
  DraggableLine,
  type DraggableLineRef,
  getOrientedWorldNormalsForIntersection,
  ImmutableLine3,
  ImmutableVector3,
  LineHandles,
  toImmutable,
  useCameraStore,
} from '@modugen/ui-toolkit'
import { Html } from '@react-three/drei'
import { useControlStore, useModelStore, useTapelineStore } from '@editorStores'
import useTapelineSnapTargets from '../hooks/useTapelineSnapTargets'
import TapelineEndTicks from './TapelineEndTicks'

const { useKey } = reactUse

const applyStoreyZToLine = (line: ImmutableLine3, z: number): ImmutableLine3 =>
  new ImmutableLine3(
    new ImmutableVector3(line.start.x, line.start.y, z),
    new ImmutableVector3(line.end.x, line.end.y, z),
  )

interface TransientLineState {
  isCreating?: boolean
  isFirstMove?: boolean
  isUpdating?: boolean
  lineRef?: React.RefObject<DraggableLineRef>
}

const TapelineElements = {
  Line: 'TapelineLine',
  Handle: 'TapelineHandle',
} as const

const minLineLength = 0.01

const TapelineV2Controller = (): ReactElement | null => {
  const tapelineStore = useTapelineStore()
  const activeFilterViewId = useTapelineStore(state => state.activeFilterViewId)
  const activeFilterViewLines = useMemo(
    () => tapelineStore.lines.filter(line => line.filterViewId === activeFilterViewId),
    [tapelineStore.lines, activeFilterViewId],
  )
  const { createTapelineFromInput, updateTapelineFields } = useTapelineMutations()
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const enableXYFeaturesInOrthographic = useCameraStore(
    state => state.enableXYFeaturesInOrthographic,
  )
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const activeStorey = useControlStore(state => state.floorplanState.storey)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const xyOnly = isOrthographic

  const storeyLowerZ = useMemo(() => {
    if (!xyOnly) return undefined
    const storey = activeStorey ?? '0'
    return storeyBoundaries[storey]?.[0]
  }, [xyOnly, activeStorey, storeyBoundaries])

  const transientLineState = useRef<TransientLineState>({})
  const [showTempLine, setShowTempLine] = useState(false)
  const [enableIndicator, setEnableIndicator] = useState(true)

  const drawControllerRef = useRef<DrawControllerRef>(null)
  const tempLineRef = useRef<DraggableLineRef>(null)
  const storedLineRefs = useRef<Map<string, React.RefObject<DraggableLineRef>>>(new Map())

  const tapelineTargets = useTapelineSnapTargets(tapeline =>
    tapeline.id
      ? tapeline.id !== tapelineStore.selectedId &&
        (tapelineStore.isVisible || tapelineStore.isActive)
      : true,
  )

  useEffect(() => {
    const currentLineIds = new Set(activeFilterViewLines.map(line => line.id))
    const refsToRemove = Array.from(storedLineRefs.current.keys()).filter(
      id => !currentLineIds.has(id),
    )
    refsToRemove.forEach(id => storedLineRefs.current.delete(id))

    activeFilterViewLines.forEach(line => {
      if (!storedLineRefs.current.has(line.id)) {
        storedLineRefs.current.set(line.id, createRef<DraggableLineRef>())
      }
    })
  }, [activeFilterViewLines])

  const onDrawStart = useCallback(
    (
      transientDrawState: Parameters<
        NonNullable<React.ComponentProps<typeof DrawControllerWrapper>['onDrawStart']>
      >[0],
    ) => {
      if (
        transientDrawState.drawPoint &&
        tempLineRef.current &&
        !transientLineState.current.lineRef
      ) {
        tapelineStore.deselectLine()
        tapelineStore.setIsDrawing(true)
        transientLineState.current = {
          isCreating: true,
          isFirstMove: true,
          lineRef: tempLineRef,
        }

        const { drawPoint, drawTarget } = transientDrawState
        if (drawPoint && drawTarget) {
          const snappingNormals = getOrientedWorldNormalsForIntersection(drawTarget, xyOnly)
          if (snappingNormals) {
            drawControllerRef.current?.setAngleSnappingOrigin({
              origin: drawPoint,
              ...snappingNormals,
            })
          }
        }
      }
    },
    [tapelineStore, xyOnly],
  )

  const onDrawMouseMove = useCallback(
    (
      transientDrawState: Parameters<
        NonNullable<React.ComponentProps<typeof DrawControllerWrapper>['onMouseMove']>
      >[0],
    ) => {
      const { validIntersections, drawPoint, angleSnappingOrigin } = transientDrawState
      const { current: transient } = transientLineState

      let actualDrawPoint = transient.isFirstMove ? angleSnappingOrigin?.origin : drawPoint

      const intersectedHandle = validIntersections?.find(
        intersection => intersection.object.name === TapelineElements.Handle,
      )

      if (intersectedHandle) {
        if (enableIndicator) setEnableIndicator(false)
        actualDrawPoint = toImmutable(intersectedHandle.object.position)
      } else if (!enableIndicator) {
        setEnableIndicator(true)
      }

      if (
        actualDrawPoint &&
        (transient.isCreating || transient.isUpdating) &&
        transient.lineRef?.current
      ) {
        if (transient.isCreating && transient.isFirstMove) {
          transient.lineRef.current.setHandleAndStartDragging(
            LineHandles.End,
            actualDrawPoint,
            actualDrawPoint,
          )
          transient.isFirstMove = false
          setShowTempLine(true)
        }

        transient.lineRef.current.updateActiveHandle(actualDrawPoint)
      }
    },
    [enableIndicator],
  )

  const onDrawEnd = useCallback(() => {
    setShowTempLine(false)
    const { current: transient } = transientLineState

    if (transient.isCreating && transient.lineRef?.current) {
      let line = transient.lineRef.current.stopDraggingAndGetLine()
      if (storeyLowerZ !== undefined) {
        line = applyStoreyZToLine(line, storeyLowerZ)
      }
      if (line.start.distanceTo(line.end) >= minLineLength) {
        createTapelineFromInput({
          ...line,
        }).catch(() => undefined)
      }
    }

    transientLineState.current = {}
    tapelineStore.setIsDrawing(false)
  }, [tapelineStore, storeyLowerZ, createTapelineFromInput])

  useKey('Escape', () => {
    setShowTempLine(false)
    drawControllerRef.current?.abortDrawing()
    drawControllerRef.current?.setAngleSnappingOrigin(undefined)
    transientLineState.current = {}
    tapelineStore.setIsDrawing(false)
  })

  const onLineSelect = useCallback(
    (tapelineId?: string) => {
      if (!tapelineId || !tapelineStore.isActive) return
      const tapeline = tapelineStore.lines.find(
        line => line.id === tapelineId && line.filterViewId === activeFilterViewId,
      )
      if (!tapeline) return
      if (tapeline.id !== tapelineStore.selectedId) {
        tapelineStore.selectLine(tapeline.id)
      }
    },
    [tapelineStore, activeFilterViewId],
  )

  const onLineEditStart = useCallback(
    (activeHandle: LineHandles, tapelineId?: string) => {
      if (!tapelineId || transientLineState.current.lineRef) return
      const tapeline = tapelineStore.lines.find(
        line => line.id === tapelineId && line.filterViewId === activeFilterViewId,
      )
      if (!tapeline) return

      const lineRef = storedLineRefs.current.get(tapeline.id)
      if (!lineRef) return

      transientLineState.current = {
        isUpdating: true,
        lineRef,
      }
      tapelineStore.setIsDrawing(true)

      if (snapToAngles) {
        const drawController = drawControllerRef.current
        const drawTarget = drawController?.transientState.drawTarget
        if (drawController && drawTarget) {
          const snappingNormals = getOrientedWorldNormalsForIntersection(drawTarget, xyOnly)
          const opposingPoint = activeHandle === LineHandles.End ? tapeline.start : tapeline.end
          if (snappingNormals && opposingPoint) {
            drawController.setAngleSnappingOrigin({
              origin: opposingPoint,
              ...snappingNormals,
            })
          }
        }
      }
    },
    [snapToAngles, tapelineStore, activeFilterViewId, xyOnly],
  )

  const onLineEditStop = useCallback(
    (tapelineId?: string) => {
      const { current: transient } = transientLineState
      if (!tapelineId || !transient.isUpdating || !transient.lineRef?.current) return

      const tapeline = tapelineStore.lines.find(
        line => line.id === tapelineId && line.filterViewId === activeFilterViewId,
      )
      if (!tapeline) return

      let { start, end } = transient.lineRef.current.stopDraggingAndGetLine()
      if (storeyLowerZ !== undefined) {
        const lineWithStoreyZ = applyStoreyZToLine(new ImmutableLine3(start, end), storeyLowerZ)
        start = lineWithStoreyZ.start
        end = lineWithStoreyZ.end
      }
      if (start.distanceTo(end) >= minLineLength) {
        updateTapelineFields(tapeline.id, { start, end })
      }

      transientLineState.current = {}
      tapelineStore.setIsDrawing(false)
    },
    [tapelineStore, activeFilterViewId, storeyLowerZ, updateTapelineFields],
  )

  const isTapelineSegment = (object?: Object3D): boolean => {
    if (!object) return false
    return (Object.values(TapelineElements) as string[]).includes(object.name)
  }

  const isValidDrawTarget = useCallback((object: Object3D): boolean => {
    let isValidTarget = !isTapelineSegment(object)
    const { current: transient } = transientLineState
    if ((transient.isCreating || transient.isUpdating) && transient.lineRef?.current) {
      isValidTarget =
        isValidTarget &&
        object !== transient.lineRef.current.startHandleRef.current &&
        object !== transient.lineRef.current.endHandleRef.current
    }
    return isValidTarget
  }, [])

  const isValidDrawStart = useCallback(
    (intersections: Intersection[]): boolean =>
      every(intersections, intersection => intersection.object.name !== TapelineElements.Line),
    [],
  )

  const additionalSnapTargets = useMemo(
    () => [...(tapelineStore.additionalSnapTargets || []), ...tapelineTargets],
    [tapelineStore.additionalSnapTargets, tapelineTargets],
  )

  const defaultTempLine = useMemo(
    () => new ImmutableLine3(new ImmutableVector3(), new ImmutableVector3()),
    [],
  )

  return tapelineStore.isActive || tapelineStore.isVisible ? (
    <>
      <DrawControllerWrapper
        ref={drawControllerRef}
        enabled={tapelineStore.isActive}
        enableIndicator={enableIndicator}
        snapToCornersAndEdges={snapToCornersAndEdges}
        snapToAngles={snapToAngles}
        orthoSnap={snapOrthogonal && isOrthographic && enableXYFeaturesInOrthographic}
        xyOnly={xyOnly}
        additionalSnapTargets={additionalSnapTargets}
        onDrawStart={onDrawStart}
        onMouseMove={onDrawMouseMove}
        onDrawEnd={onDrawEnd}
        isValidDrawTarget={isValidDrawTarget}
        isValidDrawStart={isValidDrawStart}
        indicatorType={tapelineStore.indicatorType}
      />

      <DraggableLine
        ref={tempLineRef}
        line={defaultTempLine}
        lineId={-1}
        lineName={TapelineElements.Line}
        handleName={TapelineElements.Handle}
        isVisible={showTempLine}
        showIndicators={tapelineStore.showIndicators}
      />
      {isOrthographic && (
        <TapelineEndTicks
          start={defaultTempLine.start}
          end={defaultTempLine.end}
          lineRef={tempLineRef}
          color={DEFAULT_TAPLINE_STYLE.color}
          lineWidth={DEFAULT_TAPLINE_STYLE.thickness}
          visible={showTempLine}
        />
      )}

      {activeFilterViewLines.map(tapeline => {
        const lineRef = storedLineRefs.current.get(tapeline.id)
        const isSelected = tapelineStore.selectedId === tapeline.id
        const showLine = tapelineStore.isVisible || tapelineStore.isActive
        const showStartText = tapelineStore.isVisible && !!tapeline.text?.start
        const showEndText = tapelineStore.isVisible && !!tapeline.text?.end
        const showCenterText = tapelineStore.isVisible && !!tapeline.text?.center
        const centerPosition: [number, number, number] = [
          (tapeline.start.x + tapeline.end.x) / 2,
          (tapeline.start.y + tapeline.end.y) / 2,
          (tapeline.start.z + tapeline.end.z) / 2,
        ]

        return (
          <group key={tapeline.id}>
            <DraggableLine
              ref={lineRef}
              line={tapeline}
              lineName={TapelineElements.Line}
              handleName={TapelineElements.Handle}
              color={tapeline.style.color}
              isVisible={showLine}
              isSelected={isSelected}
              editable={isSelected}
              hoverable={tapelineStore.isActive}
              showLengthIndicator={
                tapeline.showLengthLabel && (tapelineStore.isVisible || tapelineStore.isActive)
              }
              showIndicators={tapelineStore.showIndicators && tapelineStore.isActive}
              onEditStart={handle => onLineEditStart(handle, tapeline.id)}
              onEditStop={() => onLineEditStop(tapeline.id)}
              onClick={() => onLineSelect(tapeline.id)}
              showHandles={isSelected}
              lineThreeProps={{
                lineWidth: tapeline.style.thickness,
                ...(tapeline.style.lineStyle === 'dashed'
                  ? { dashed: true, dashSize: 0.2, gapSize: 0.1 }
                  : tapeline.style.lineStyle === 'dashed-long-gap'
                  ? { dashed: true, dashSize: 0.2, gapSize: 0.2 }
                  : {}),
              }}
            />
            {isOrthographic && (
              <TapelineEndTicks
                start={tapeline.start}
                end={tapeline.end}
                lineRef={isSelected ? lineRef : undefined}
                color={tapeline.style.color}
                lineWidth={tapeline.style.thickness}
                visible={showLine}
              />
            )}

            {showStartText && (
              <mesh position={tapeline.start.v}>
                <Html
                  style={{
                    pointerEvents: 'none',
                    color: tapeline.style.color,
                    transform: 'translate(-50%, -120%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tapeline.text?.start}
                </Html>
              </mesh>
            )}

            {showEndText && (
              <mesh position={tapeline.end.v}>
                <Html
                  style={{
                    pointerEvents: 'none',
                    color: tapeline.style.color,
                    transform: 'translate(-50%, -120%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tapeline.text?.end}
                </Html>
              </mesh>
            )}

            {showCenterText && (
              <mesh position={centerPosition}>
                <Html
                  style={{
                    pointerEvents: 'none',
                    color: tapeline.style.color,
                    transform: 'translate(-50%, -120%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tapeline.text?.center}
                </Html>
              </mesh>
            )}
          </group>
        )
      })}
    </>
  ) : null
}

export default TapelineV2Controller
